/*
  Starting a policy while on the phone to the customer.

  Two panes in ONE modal, never a route change. The agent is mid-conversation and a page
  transition is a silence they have to fill — so the quote form and the prices it produces live
  in the same place, and the second pane replaces the first the moment the server answers.

  The policy EXISTS after the first pane. That is why "Close — I'll finish this later" is offered
  rather than a Cancel: a customer who rings off halfway has a quote sitting on the worklist at
  `quoted`, resumable by anyone, not a lost call.
*/
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Space } from 'antd'
import PolicyPrices, { chooseInsurer } from './PolicyPrices'
import {
  postRequest,
  getRequest,
  insuranceStartPolicyUrl,
  insurancePolicyTypesUrl,
  insuranceVehicleClassesUrl,
  errorNotification
} from '../../config'

const { Option } = Select

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

// The code the API uses for the cover that prices a percentage of the car's value. The vehicle
// value question exists for this one and no other — a value typed against a third-party quote is
// a figure nobody asked for sitting in a record that gets underwritten.
const COMPREHENSIVE = 'comprehensive'

const NewPolicyModal = ({ visible, onClose, onCreated }) => {
  const [form] = Form.useForm()
  const [busy, setBusy] = useState(false)
  const [policy, setPolicy] = useState(null)
  const [cards, setCards] = useState([])
  const [policyTypes, setPolicyTypes] = useState([])
  const [vehicleClasses, setVehicleClasses] = useState([])
  const [cover, setCover] = useState(null)

  // Loaded when the modal first opens rather than on mount: this is a form the agent may never
  // open in a session, and two requests for a form nobody opened is two requests. Guarded on
  // `policyTypes.length` so reopening it does not fetch the same two lists again.
  useEffect(() => {
    if (!visible || policyTypes.length) return
    const load = async () => {
      const [types, classes] = await Promise.all([
        getRequest(`${insurancePolicyTypesUrl}/`, 'cover types', () => {}),
        getRequest(`${insuranceVehicleClassesUrl}/?active=true`, 'vehicle types', () => {})
      ])
      if (types.success) setPolicyTypes(types.data.results || types.data || [])
      if (classes.success) setVehicleClasses(classes.data.results || classes.data || [])
    }
    load()
    // eslint-disable-next-line
  }, [visible])

  const reset = () => {
    form.resetFields()
    setPolicy(null)
    setCards([])
    setCover(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const chosenCover = policyTypes.find(row => row.id === cover)
  const needsValue = chosenCover?.code === COMPREHENSIVE

  const quote = async values => {
    setBusy(true)
    const response = await postRequest(
      insuranceStartPolicyUrl,
      {
        phone: values.phone,
        email: values.email || '',
        policyType: values.policyType,
        vehicleClass: values.vehicleClass,
        seats: values.seats,
        // Sent only when the cover asks for it. The server refuses it otherwise, which is the
        // behaviour we want — it is the same refusal the app gets.
        ...(needsValue ? { vehicleValue: String(values.vehicleValue) } : {})
      },
      'quote',
      () => {}
    )
    setBusy(false)
    if (!response.success) {
      const field = response.error?.response?.data?.field
      const detail = serverSaid(response) || 'That could not be quoted.'
      if (field) {
        // Under the field it belongs to. `field` is the server's own name for it, snake_case, and
        // the form's names are camelCase — mapped rather than guessed, because a mismatch would
        // silently show no error at all.
        const names = {
          phone: 'phone',
          policy_type: 'policyType',
          vehicle_class: 'vehicleClass',
          seats: 'seats',
          vehicle_value: 'vehicleValue'
        }
        form.setFields([{ name: names[field] || field, errors: [detail] }])
      }
      errorNotification(detail)
      return
    }
    setPolicy(response.data.policy)
    setCards(response.data.cards || [])
  }

  // Shared with the drawer's own prices panel, so a policy quoted here and finished there is
  // chosen the same way — same confirmation, same figure, same server call.
  const choose = card =>
    chooseInsurer(policy.id, card, created => {
      reset()
      onCreated(created)
    })

  return (
    <Modal
      visible={visible}
      width={policy ? 720 : 560}
      title={policy ? `Prices for ${policy.phone}` : 'New insurance policy'}
      afterClose={reset}
      onCancel={close}
      footer={
        policy ? (
          <Button onClick={close}>Close — I&apos;ll finish this later</Button>
        ) : (
          <Space>
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" loading={busy} onClick={() => form.submit()}>
              Get prices
            </Button>
          </Space>
        )
      }
      destroyOnClose
    >
      {policy ? (
        <PolicyPrices cards={cards} onChoose={choose} />
      ) : (
        <Form form={form} layout="vertical" onFinish={quote} initialValues={{ seats: 5 }}>
          <Divider orientation="left" plain>
            Customer
          </Divider>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="WhatsApp number"
                rules={[{ required: true, message: 'The policy hangs on this number.' }]}
                extra="What the certificate goes to, and how they find this in the app later."
              >
                <Input placeholder="0244 123 456" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="Email (optional)">
                <Input placeholder="name@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            The car
          </Divider>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="policyType"
                label="Cover"
                rules={[{ required: true, message: 'Which cover?' }]}
              >
                <Select placeholder="Choose a cover" onChange={setCover}>
                  {policyTypes.map(row => (
                    <Option key={row.id} value={row.id}>
                      {row.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="vehicleClass"
                label="Vehicle type"
                rules={[{ required: true, message: 'Which type of vehicle?' }]}
              >
                <Select placeholder="Choose a type">
                  {vehicleClasses.map(row => (
                    <Option key={row.id} value={row.id}>
                      {row.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="seats"
                label="Seats"
                extra="Including the driver's."
                rules={[{ required: true, message: 'How many seats?' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {/*
              Appears and disappears with the cover, which is not a nicety — the server refuses a
              value on a third-party quote, so a field left on screen would be a field that
              produces a 400 the agent cannot explain to the customer waiting on the line.
            */}
            {needsValue && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="vehicleValue"
                  label="Vehicle value"
                  extra="What it would cost to replace today."
                  rules={[{ required: true, message: 'Comprehensive is priced on this.' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      )}
    </Modal>
  )
}

NewPolicyModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired
}

export default NewPolicyModal
